import type { AudioTrack, Playlist, RadioName, RadiosConfig } from "./audio/types";
import { ICONS } from "./interactive/icons";
import {
	NavDirection,
	NavigationData,
	Position,
	SceneComponent,
	SceneComponentConfig,
	SceneInteractivesConfig,
	SceneInteractivesUIConfig,
	Verb,
} from "./interactive/types";
import {
	BACK_DEFAULT_POSITION,
	CENTER_ARROW_DEFAULT_POSITION,
	GLOBAL_FOCUS_IDS,
	HOME_DEFAULT_POSITION,
	LEFT_ARROW_DEFAULT_POSITION,
	RIGHT_ARROW_DEFAULT_POSITION,
} from "./interactive/constants";
import { ALL_RADIOS } from "./audio/radioConfig";
import { navGraphUpdateEdges, navGraphUpdateInitialFocus } from "./interactive/navGraphHelpers";

type ArrowConfig = {
	id: string;
	verbs: Verb[];
	direction: NavDirection;
	hitbox?: Position;
};

const EMPTY_NAVIGATION: NavigationData = {
	id: "",
	up: null,
	down: null,
	left: null,
	right: null,
	initialFocus: false,
} as const;

export const calculateArrowRotation = (dir: NavDirection) => {
	if (dir === "left") return -90;
	if (dir === "right") return 90;
	if (dir === "up") return 0;
	if (dir === "down") return 180;
	return 0;
};

/**
 * Helper function to generate configuration values for default Arrow components
 */
export const sceneComponentsDefaultArrowConfig = ({
	id,
	verbs,
	direction,
	hitbox,
}: ArrowConfig) => {
	let position = CENTER_ARROW_DEFAULT_POSITION;
	if (direction === "left") position = LEFT_ARROW_DEFAULT_POSITION;
	if (direction === "right") position = RIGHT_ARROW_DEFAULT_POSITION;

	const rotation = calculateArrowRotation(direction);

	return {
		id,
		verbs,
		direction,
		position: {
			...position,
			icon: {
				...position.icon,
				rotation,
			},
			hitbox: {
				...position.hitbox,
				...hitbox,
			},
		},
		src: ICONS.ARROW,
		protected: false,
	};
};

export const newPlaylist = (
	playlist: AudioTrack[],
	gain: number,
	isLooping = true,
	name: string,
): Playlist => ({
	gain,
	playlist,
	isLooping,
	name,
});

export const radioConfig = (sceneConfig: Partial<Record<RadioName, number>>): RadiosConfig[] => {
	return ALL_RADIOS.map((name) => {
		if (sceneConfig[name]) {
			return {
				name: name,
				gain: sceneConfig[name],
			};
		}
		return {
			name: name,
			gain: 0,
		};
	});
};

/**
 * THE FOLLOWING SHOULD PROBABLY BE SEPERATED OUT INTO A DIFFERENT FILE
 * ALL OF THESE FUNCTIONS ARE FOR CONVERTING FROM CONFIG TO RUNTIME VALUES
 */

/**
 * Generates default 'NAV_MENU' components for a scene.
 *
 * Includes the `BACK` and `HOME` components
 *
 * @returns An array of scene components representing the navigation menu.
 */
const sceneComponentsGenerateNavMenu = (): SceneComponent[] => {
	return [
		{
			src: ICONS.BACK.DEFAULT,
			protected: false,
			position: BACK_DEFAULT_POSITION,
			navigation: {
				...EMPTY_NAVIGATION,
				id: GLOBAL_FOCUS_IDS.BACK,
			},
			verbs: [{ name: "BACK" as const }],
			hoveredSrc: ICONS.BACK.HOVER,
		},
		{
			src: ICONS.HOME.DEFAULT,
			protected: false,
			position: HOME_DEFAULT_POSITION,
			navigation: {
				...EMPTY_NAVIGATION,
				id: GLOBAL_FOCUS_IDS.HOME,
			},
			verbs: [
				{
					name: "NAVIGATE" as const,
					data: {
						url: "/",
					},
				},
			],
			hoveredSrc: ICONS.HOME.HOVER,
		},
	];
};

/**
 * Generates a `USER_PROFILE` component if it is visible.
 *
 * @param isVisible - Whether the user profile should be included in the scene.
 * @returns A array with a single user profile component or an empty array.
 */
const sceneComponentsGenerateUserProfile = (): SceneComponent[] => {
	return [
		{
			src: "none",
			protected: false,
			position: BACK_DEFAULT_POSITION,
			navigation: {
				...EMPTY_NAVIGATION,
				id: GLOBAL_FOCUS_IDS.USER_PROFILE,
			},
			verbs: [],
			isUserProfile: true,
		},
	];
};

/**
 * Converts interactive component config into runtime scene components.
 *
 * Adds default navigation data to each component.
 *
 * @param config - the interactive components configuration.
 * @returns unordered list of runtime scene component data
 */
const sceneComponentsBuildInteractives = (
	config: Record<string, SceneComponentConfig> | undefined,
): SceneComponent[] => {
	return Object.values(config ?? []).map((component) => ({
		...component,
		navigation: {
			...EMPTY_NAVIGATION,
			id: component.id,
		},
	}));
};

/**
 * Converts UI config into runtime scene components.
 *
 * This includes `NAV_MENU` and `USER_PROFILE` components if applicable.
 *
 * @param UI - The UI configuration for the scene.
 * @returns unordered list of runtime scene component data
 */
const sceneComponentsBuildUI = (UI: SceneInteractivesUIConfig): SceneComponent[] => {
	const uiComponents: SceneComponent[] = [];

	if (!UI) {
		return uiComponents;
	}

	if (!UI.NAV_MENU?.disable) {
		// assume that if no value set we want the NAV_MENU items to display
		uiComponents.push(...sceneComponentsGenerateNavMenu());
	}

	if (UI.USER_PROFILE?.isVisible) {
		uiComponents.push(...sceneComponentsGenerateUserProfile());
	}
	return uiComponents;
};

/**
 * Builds all scene components from the scene configuration.
 *
 * @param scene - The full scene configuration
 * @returns A unordered list of prepared scene components for runtime use.
 */
export const sceneComponentsBuild = (scene: SceneInteractivesConfig): SceneComponent[] => {
	const { UI, NAV_GRAPH } = scene;
	const initialFocusId = UI?.INITIAL_FOCUS ?? "";

	const uiComponents = sceneComponentsBuildUI(UI);
	const sceneComponents = sceneComponentsBuildInteractives(scene.INTERACTIVES);
	const allComponents = [...sceneComponents, ...uiComponents];

	// TODO: Purify LOGS
	if (!NAV_GRAPH && allComponents.length) {
		console.warn("no navigation graph, scene may be incompatible with arrow navigation");
	}
	if (!initialFocusId) {
		console.warn("No Initial Focused Element, scene may be incompatible with arrow navigation");
	}

	const withInitialFocus = navGraphUpdateInitialFocus(initialFocusId, allComponents);
	const connectedComponents = navGraphUpdateEdges(withInitialFocus, NAV_GRAPH ?? []);

	return connectedComponents;
};
